import {t, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextAreaBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {CalendarEventVersionResponse} from '../../../../../models/api/calendarEventVersionModel';
import {ParticipantResponseStatusesEnum} from '../../../../../models/api/participantModel';
import {ResponseStatusDropdownBox} from '../../../../calendars/viewer/responseStatusDropdownBox';
import {OkCancelDialog} from '../../../../common/dialogs/okCancelDialog';

/*
 * Props.
 */

// eslint-disable-next-line @typescript-eslint/naming-convention
interface SendACommentDialogProps {
  response: CalendarEventVersionResponse;
  onSendClick: HandlerOf<CalendarEventVersionResponse>;
  onCancelClick: Handler;
}

/*
 * Styles.
 */

const StyledTextAreaBoxDiv = styled.div`
  margin-top: -12px;

  textarea {
    overflow-y: scroll;
    min-height: 70px;
  }
`;

const StyledWrapperDiv = styled.div`
  width: fit-content;
  margin-bottom: 10px;
`;

const StyledResponseStatusDropdownBox = styled(ResponseStatusDropdownBox)`
  margin: 0px;

  & > :first-child {
    margin-right: 0px;
  }
`;

/*
 * Component.
 */

export const SendACommentDialog: FC<SendACommentDialogProps> = (props) => {
  const {address, status, comment} = props.response;
  const [responseComment, setResponseComment] = useState(comment);
  const [responseStatus, setResponseStatus] = useState<ParticipantResponseStatusesEnum>(status);
  const hasResponseStatus = [
    ParticipantResponseStatusesEnum.ACCEPTED,
    ParticipantResponseStatusesEnum.TENTATIVE,
    ParticipantResponseStatusesEnum.DECLINED,
  ].includes(responseStatus);
  const localize = useLocalize();

  const onOkClick = () => {
    const calendarEventResponse: CalendarEventVersionResponse = {
      address,
      status: responseStatus,
      comment: responseComment,
    };
    props.onSendClick(calendarEventResponse);
  };

  return (
    <OkCancelDialog
      width={400}
      title={<Trans id="send-a-comment-dialog-title">Send note</Trans>}
      okButtonLabel={<Trans id="send-a-comment-dialog-send-label">Send</Trans>}
      isOkDisabled={!hasResponseStatus}
      onOkClick={onOkClick}
      onRequestClose={props.onCancelClick}
      isCaptureInputsDisabled
    >
      <StyledTextAreaBoxDiv>
        <TextAreaBox
          size={VisualSizesEnum.MEDIUM}
          value={responseComment}
          onChange={setResponseComment}
          placeholder={localize(t('send-a-comment-dialog-placeholder')`Type your note`)}
          shouldAutoFocus
          maxLength={500}
        />
      </StyledTextAreaBoxDiv>
      <StyledWrapperDiv>
        <StyledResponseStatusDropdownBox
          responseStatus={responseStatus}
          onResponseStatusChange={setResponseStatus}
        />
      </StyledWrapperDiv>
    </OkCancelDialog>
  );
};
